import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useFormik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { routes } from '../../constants/routes';
import { IResetPasswordCredentials } from '../../schemas/apis/interface';
import { resetPasswordApi } from '../../utils/apis';
import quibbleaiLogo from '../../assets/images/quibbleaiLogoDark.svg';
import { resetPasswordSchema } from '../../schemas';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const params = useParams();

  const handleCloseAlert = useCallback(() => {
    setIsAlertOpen(false);
    setAlertText('');
  }, []);

  const handleOpenAlert = useCallback(
    (message: string, severity: 'success' | 'error') => {
      setAlertText(message);
      setAlertSeverity(severity);
      setIsAlertOpen(true);
    },
    []
  );

  const initialValues: IResetPasswordCredentials = {
    password: '',
    passwordConfirm: '',
    token: params.token ?? '',
  };

  const { isPending, error, mutateAsync } = useMutation({
    mutationKey: ['reset-password', params.token],
    mutationFn: resetPasswordApi,
    onSuccess: () => {
      resetForm();
      handleOpenAlert(
        'Your password has been reset. You can now login with your new password.',
        'success'
      );
      setTimeout(() => {
        navigate(routes.login);
      }, 2000);
    },
  });

  const {
    resetForm,
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: values => {
      const token = params.token;
      mutateAsync({
        ...values,
        token: token ? token : '',
      });
    },
  });

  const submitHandler = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  useEffect(() => {
    if (error) {
      if (isAxiosError(error) && error?.message === 'Network Error') {
        handleOpenAlert('Network Error', 'error');
      }

      if (isAxiosError(error) && error.response && error.response.data) {
        const errorMessage = (error.response.data as { message: string })
          .message;
        handleOpenAlert(errorMessage, 'error');
      }
    }
  }, [error, handleOpenAlert]);

  useEffect(() => {
    document.title = 'Reset Password - Quibble AI';
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Snackbar
        open={isAlertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertText}
        </Alert>
      </Snackbar>

      <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
        <img src={quibbleaiLogo} alt="Quibble AI" style={{ width: '8rem' }} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: '463px',
          height: '362px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: '12px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: '20px',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Setup A New Password
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '14px',
            textAlign: 'center',
            mb: 3,
          }}
        >
          Create a strong password to keep your account secure
        </Typography>

        <form onSubmit={submitHandler} style={{ width: '100%' }}>
          <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Enter New Password
              </Typography>
              <TextField
                fullWidth
                id="password"
                size="small"
                name="password"
                type={isPasswordVisible ? 'text' : 'password'}
                value={values.password}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    width: '399px',
                    borderRadius: '6px',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        edge="end"
                      >
                        {isPasswordVisible ? (
                          <EyeSlash size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                id="passwordConfirm"
                size="small"
                name="passwordConfirm"
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                value={values.passwordConfirm}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  touched.passwordConfirm && Boolean(errors.passwordConfirm)
                }
                helperText={touched.passwordConfirm && errors.passwordConfirm}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    width: '399px',
                    borderRadius: '6px',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                        }
                        edge="end"
                      >
                        {isConfirmPasswordVisible ? (
                          <EyeSlash size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              fullWidth
              type="submit"
              disabled={isPending}
              variant="contained"
              color="primary"
              sx={{
                mt: 2,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  scale: 1.01,
                  boxShadow: 'none',
                },
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: 'none',
                py: 1.5,
                height: '34px',
              }}
            >
              {isPending ? <CircularProgress size={24} /> : 'Save Password'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default React.memo(ResetPasswordPage);
